import { Capacitor } from "@capacitor/core";
import { getMessaging, getToken, isSupported, onMessage } from "firebase/messaging";
import { get, ref, update } from "firebase/database";

import { db } from "../firebaseConfig";
import { showToast } from "../components/NotifyToast.jsx";
import { resolvePushNavigation } from "../utils/pushNavigation.js";

const VAPID_KEY = import.meta.env.VITE_FIREBASE_VAPID_KEY || "";

let initForUid = null;
let webListenerAttached = false;
let nativeListenersAttached = false;
let nativeBridgeAttached = false;
let nativeTapAttached = false;
const PUSH_INSTALLATION_ID_KEY = "trekkin.push.installationId.v1";
const PUSH_REMINDER_KEY = "trekkin.push.permissionReminder.v1";
const PUSH_REMINDER_INTERVAL_MS = 30 * 24 * 60 * 60 * 1000;

async function getPushPermissionAndRelease() {
  let release = "web";
  if (Capacitor.isNativePlatform()) {
    const [{ PushNotifications }, { App }] = await Promise.all([
      import("@capacitor/push-notifications"),
      import("@capacitor/app"),
    ]);
    const [permission, info] = await Promise.all([
      PushNotifications.checkPermissions(),
      App.getInfo().catch(() => null),
    ]);
    release = `${String(info?.version || "native")}:${String(info?.build || "")}`;
    return { granted: permission?.receive === "granted", release };
  }
  if (typeof Notification === "undefined") return { granted: true, release };
  return {
    granted: Notification.permission === "granted",
    release: String(import.meta.env.VITE_BUILD_TIME || "web"),
  };
}

async function maybeShowPushPermissionReminder() {
  if (typeof window === "undefined") return;
  try {
    const { granted, release } = await getPushPermissionAndRelease();
    if (granted) {
      localStorage.removeItem(PUSH_REMINDER_KEY);
      return;
    }

    let previous = {};
    try { previous = JSON.parse(localStorage.getItem(PUSH_REMINDER_KEY) || "{}"); } catch {}
    const now = Date.now();
    const isNewRelease = String(previous?.release || "") !== String(release || "");
    const reminderExpired = now - Number(previous?.lastShownAt || 0) >= PUSH_REMINDER_INTERVAL_MS;
    if (!isNewRelease && !reminderExpired) return;

    localStorage.setItem(PUSH_REMINDER_KEY, JSON.stringify({ release, lastShownAt: now }));
    const isEnglish = String(navigator?.language || "").toLowerCase().startsWith("en");
    showToast(
      isEnglish
        ? "Enable notifications in Account > Settings to receive alerts, messages and important updates."
        : "Ativa as notificações em Conta > Definições para receber alertas, mensagens e atualizações importantes.",
      "warning",
      8000
    );
  } catch (err) {
    console.warn("Não foi possível verificar o lembrete de notificações:", err);
  }
}

export async function clearNativeAppBadge() {
  if (typeof window === "undefined" || !Capacitor.isNativePlatform()) return;

  // A bridge iOS limpa o número imediatamente, antes de qualquer novo cálculo
  // dos contadores internos da aplicação.
  try {
    window?.webkit?.messageHandlers?.nativeBridge?.postMessage({
      action: "setAppBadge",
      count: 0,
    });
  } catch {}

  try {
    await window?.Capacitor?.Plugins?.Badge?.set?.({ count: 0 });
  } catch {}

  // Em Android o badge/dot é normalmente derivado das notificações ainda
  // entregues. Removê-las ao entrar na app evita que o launcher preserve um
  // contador antigo. No iOS isto também limpa notificações já apresentadas.
  try {
    const { PushNotifications } = await import("@capacitor/push-notifications");
    await PushNotifications.removeAllDeliveredNotifications();
  } catch (err) {
    console.warn("Não foi possível limpar as notificações entregues:", err);
  }
}

function getPushInstallationId() {
  if (typeof window === "undefined") return "";
  try {
    let value = String(localStorage.getItem(PUSH_INSTALLATION_ID_KEY) || "").trim();
    if (!value) {
      value = globalThis.crypto?.randomUUID?.() ||
        `push-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
      localStorage.setItem(PUSH_INSTALLATION_ID_KEY, value);
    }
    return value;
  } catch {
    return "";
  }
}

function handlePushNavigation(data, openPanel) {
  if (typeof openPanel !== "function" || !data) return;
  const destination = resolvePushNavigation(data);
  if (destination) openPanel(destination.panel, destination.props);
}

async function saveToken(uid, token, platform) {
  if (!uid || !token) return;
  const now = Date.now();
  let appPlatform = platform || "unknown";
  let appVersion = "";
  try {
    if (Capacitor.isNativePlatform()) {
      appPlatform = Capacitor.getPlatform?.() || appPlatform;
      const { App } = await import("@capacitor/app");
      const info = await App.getInfo();
      appVersion = String(info?.version || info?.build || "").trim();
    }
  } catch {}
  const installationId = getPushInstallationId();
  const updates = {};
  // A mesma instalação pode receber um token novo após reinstalações, updates
  // ou recuperação da permissão. Remove só os tokens deste equipamento; nunca
  // interfere com os restantes dispositivos do utilizador.
  if (installationId) {
    try {
      const snap = await get(ref(db, `users/${uid}/fcmTokens`));
      const existing = snap.val() || {};
      Object.entries(existing).forEach(([existingToken, data]) => {
        if (existingToken !== token && data?.installationId === installationId) {
          updates[`users/${uid}/fcmTokens/${existingToken}`] = null;
        }
      });
    } catch {}
  }
  updates[`users/${uid}/fcmTokens/${token}`] = {
    platform: appPlatform,
    ts: now,
    lastSeenAt: now,
    updatedAt: now,
    appVersion: appVersion || null,
    installationId: installationId || null,
    userAgent:
      typeof navigator !== "undefined" ? String(navigator.userAgent || "") : "",
  };
  await update(ref(db), updates);
}

async function removeTokensForCurrentInstallation(uid) {
  const installationId = getPushInstallationId();
  if (!uid || !installationId) return 0;
  const snap = await get(ref(db, `users/${uid}/fcmTokens`));
  const existing = snap.val() || {};
  const updates = {};
  Object.entries(existing).forEach(([token, data]) => {
    if (data?.installationId === installationId) {
      updates[`users/${uid}/fcmTokens/${token}`] = null;
    }
  });
  if (!Object.keys(updates).length) return 0;
  await update(ref(db), updates);
  return Object.keys(updates).length;
}

async function registerWebPush(uid) {
  if (typeof window === "undefined" || !("serviceWorker" in navigator)) return;
  const supported = await isSupported();
  if (!supported) return;
  if (!VAPID_KEY) {
    console.warn("FCM web: VAPID key em falta (VITE_FIREBASE_VAPID_KEY)");
    return;
  }

  const registration =
    (await navigator.serviceWorker.getRegistration("/app/")) ||
    (await navigator.serviceWorker.getRegistration());
  if (!registration) return;
  const messaging = getMessaging();

  const token = await getToken(messaging, {
    vapidKey: VAPID_KEY,
    serviceWorkerRegistration: registration,
  });

  if (token) await saveToken(uid, token, "web");

  if (!webListenerAttached) {
    onMessage(messaging, (payload) => {
      const title = payload?.notification?.title || "Nova mensagem";
      const body =
        payload?.notification?.body || "Tens uma nova mensagem direta";
      const data = payload?.data || {};

      // No browser em primeiro plano o FCM não apresenta a notificação do
      // sistema automaticamente. Antes, este callback lia os dados e não fazia
      // nada, tornando as mensagens invisíveis para quem tinha a app aberta.
      showToast(`${title}: ${body}`, "info", 5200);

      // Mantém os dados disponíveis para integrações da interface sem abrir o
      // painel à força enquanto o utilizador está a trabalhar noutro ecrã.
      window.dispatchEvent(
        new CustomEvent("trekkin:foregroundPush", {
          detail: { title, body, data },
        })
      );
    });
    webListenerAttached = true;
  }
}

async function registerNativePush(uid, openPanel) {
  if (!Capacitor.isNativePlatform()) return;

  let PushNotifications;
  try {
    ({ PushNotifications } = await import("@capacitor/push-notifications"));
  } catch (err) {
    console.warn("PushNotifications plugin não disponível:", err);
    return;
  }

  const permStatus = await PushNotifications.checkPermissions();
  if (permStatus.receive !== "granted") {
    await PushNotifications.requestPermissions();
  }

  await PushNotifications.register();

  if (!nativeListenersAttached) {
    PushNotifications.addListener("registration", async (token) => {
      const platform = Capacitor.getPlatform();
      if (platform === "ios") {
        // iOS aqui devolve APNs token; o FCM vem do bridge nativo (native:pushToken).
        return;
      }
      const currentUid = initForUid;
      if (currentUid) await saveToken(currentUid, token.value, platform);
    });

    PushNotifications.addListener("registrationError", (err) => {
      console.warn("Push registration error:", err);
    });

    PushNotifications.addListener("pushNotificationReceived", (notification) => {
      const title = notification?.title || "Trekkin";
      const body = notification?.body || "Tens uma nova notificação.";
      const data = notification?.data || {};
      showToast(`${title}: ${body}`, "info", 5200);
      window.dispatchEvent(
        new CustomEvent("trekkin:foregroundPush", {
          detail: { title, body, data },
        })
      );
    });

    PushNotifications.addListener(
      "pushNotificationActionPerformed",
      (event) => {
        const data = event?.notification?.data || {};
        handlePushNavigation(data, openPanel);
      }
    );

    nativeListenersAttached = true;
  }
}

export async function initPushNotifications({ user, openPanel }) {
  const uid = user?.uid;
  if (!uid || initForUid === uid) return;

  initForUid = uid;

  if (typeof window !== "undefined" && !nativeBridgeAttached) {
    window.addEventListener("native:pushToken", (event) => {
      const detail = event?.detail || {};
      const token = detail.token;
      const platform = detail.platform || "ios";
      const currentUid = initForUid;
      if (token && currentUid) {
        saveToken(currentUid, token, platform).catch((err) => {
          console.warn("FCM native token save falhou:", err);
        });
      }
    });

    // Pede o token ao WKWebView quando o listener já está pronto (evita perder o evento)
    const iosBridge = window?.webkit?.messageHandlers?.nativeBridge;
    const iosSafeMode =
      window?.__TREKKIN_IOS_SAFE_MODE__ === true ||
      window?.localStorage?.getItem?.("trekkin.ios.safeMode") === "1";
    const disableNativePushBridge =
      window?.localStorage?.getItem?.("trekkin.ios.disableNativePushBridge") === "1";
    if (iosBridge?.postMessage && !iosSafeMode && !disableNativePushBridge) {
      iosBridge.postMessage({ action: "requestPushToken" });
      iosBridge.postMessage({ action: "requestPendingPushTap" });
    }

    nativeBridgeAttached = true;
  }

  if (typeof window !== "undefined" && !nativeTapAttached) {
    window.addEventListener("native:pushTap", (event) => {
      const userInfo = event?.detail?.userInfo || {};
      const data = userInfo?.data || userInfo;
      handlePushNavigation(data, openPanel);
    });
    nativeTapAttached = true;
  }

  try {
    await registerWebPush(uid);
  } catch (err) {
    console.warn("FCM web init falhou:", err);
  }

  try {
    await registerNativePush(uid, openPanel);
  } catch (err) {
    console.warn("FCM native init falhou:", err);
  }

  await maybeShowPushPermissionReminder();
}

export async function reactivatePushNotifications({ user, openPanel }) {
  const uid = user?.uid;
  if (!uid) throw new Error("push_user_required");
  initForUid = uid;

  if (typeof window !== "undefined") {
    // Esta flag é usada para diagnóstico; uma ação explícita do utilizador deve
    // voltar a permitir que a bridge nativa peça o token.
    try {
      localStorage.removeItem("trekkin.ios.disableNativePushBridge");
    } catch {}
  }

  if (Capacitor.isNativePlatform()) {
    const { PushNotifications } = await import("@capacitor/push-notifications");
    let permission = await PushNotifications.checkPermissions();
    if (permission.receive !== "granted") {
      permission = await PushNotifications.requestPermissions();
    }
    if (permission.receive !== "granted") {
      return { ok: false, reason: "permission-denied" };
    }
  } else if (typeof Notification !== "undefined") {
    let permission = Notification.permission;
    if (permission === "default") permission = await Notification.requestPermission();
    if (permission !== "granted") {
      return { ok: false, reason: "permission-denied" };
    }
  }

  const removedTokens = await removeTokensForCurrentInstallation(uid);
  // Força uma nova tentativa mesmo que a inicialização automática já tenha corrido.
  await registerWebPush(uid);
  await registerNativePush(uid, openPanel);

  const iosBridge = window?.webkit?.messageHandlers?.nativeBridge;
  if (iosBridge?.postMessage) {
    iosBridge.postMessage({ action: "requestPushToken" });
  }

  return { ok: true, removedTokens };
}
